import {
  OptionInfoView,
  OptionMultipleView,
  OptionPointView,
  OptionStatusView,
  OptionView,
} from './OptionView';
import { optionIsActive } from './selector/optionIsActive';
import {
  ArmyList,
  ArmyListUnit,
  ArmyListUnitOption,
  MagicItem,
  MagicStandard,
  ModelRule,
  Nullable,
  Unit,
  UnitOption,
  UnitOptionAvailabilityAction,
  UnitOptionCategory,
  UnitOptionGroup,
  UnitOptionUsePoint,
} from '@Models';
import { computeOptionPoints } from './selector/computeOptionPoints';

export const getMaxSelectableChilds = (
  option: UnitOption,
  armyListUnitOptions: Array<ArmyListUnitOption>,
): Nullable<number> => {
  const modifiers = option.availabilities.filter(
    (item) => item.action === UnitOptionAvailabilityAction.ChangeChoiseLimit,
  );
  if (modifiers.length > 0) {
    const choises = modifiers
      .filter((item) =>
        armyListUnitOptions.some((o) => o.unit_option_id === item.unitOptionId),
      )
      .map((item) => item.value)
      .filter((item): item is number => !!item);

    if (choises.length > 0) return Math.max.apply(null, choises);
  }

  if (option.hasChoises && option.hasChoises > 0) {
    return option.hasChoises;
  }

  return null;
};

export const optionPresenter = (
  armyListUnit: ArmyListUnit,
  armyList: ArmyList,
  armyListUnitOptions: Array<ArmyListUnitOption>,
  profondeur: number,
  unitOptionGroup: UnitOptionGroup,
  mount: Unit | null,
  option: UnitOption,
  parentId: number | null,
  modelRules: ModelRule[],
  magicItems: MagicItem[],
  magicStandards: MagicStandard[],
): OptionView[] => {
  const childs = unitOptionGroup.options
    .filter((item) => item.parent_id === option.id)
    .filter((item) =>
      optionIsActive(
        armyListUnit,
        armyList,
        armyListUnitOptions,
        item,
        unitOptionGroup.options,
      ),
    )
    .map((item) =>
      optionPresenter(
        armyListUnit,
        armyList,
        armyListUnitOptions,
        profondeur + 1,
        unitOptionGroup,
        mount,
        item,
        option.id,
        modelRules,
        magicItems,
        magicStandards,
      ),
    );

  const points = computeOptionPoints(option, armyListUnitOptions) ?? 0;

  return [
    {
      id: option.id,
      unitId: armyListUnit.unit_id,
      parentId: parentId,
      profondeur: profondeur,
      name: option.name,
      weight: option.weight,
      quantity: 1,
      points: option.canBeSelected
        ? ({
            current: points,
            source: points,
            isLimit: option.use_points === UnitOptionUsePoint.UpTo,
          } satisfies OptionPointView as OptionPointView)
        : null,
      maxSelectedChilds: getMaxSelectableChilds(option, armyListUnitOptions),
      minSelectedChilds:
        option.category === UnitOptionCategory.MustChoice ? 1 : 0,
      isMultiple: option.is_multiple && option.max != 1,
      isPerModel: option.is_per_model,
      multiple: {
        min: 0,
        current: 0,
        noLimit: option.max == null || option.max == 0,
        max: option.max,
        delta: option.max,
      } satisfies OptionMultipleView as OptionMultipleView,
      status: {
        isVisible: true,
        canSelect: option.canBeSelected,
        isChecked: false,
        isHidden: false,
      } satisfies OptionStatusView as OptionStatusView,
      infos: [
        ...modelRules
          .filter((item) => option.modelRuleIds.includes(item.id))
          .map(
            (item) =>
              ({
                id: item.id,
                name: item.name,
                description: item.description,
              }) satisfies OptionInfoView as OptionInfoView,
          ),
        ...magicItems
          .filter((item) => option.magicItemId === item.id)
          .map(
            (item) =>
              ({
                id: `magic-item-${item.id}`,
                name: item.name,
                description: item.description,
              }) satisfies OptionInfoView as OptionInfoView,
          ),
        ...magicStandards
          .filter((item) => option.magicStrandardId === item.id)
          .map(
            (item) =>
              ({
                id: `magic-standard-${item.id}`,
                name: item.name,
                description: item.description,
              }) satisfies OptionInfoView as OptionInfoView,
          ),
      ],
    } satisfies OptionView as OptionView,
    ...childs,
  ].flat();
};

export const optionEditPresenter = (
  armyList: ArmyList,
  armyListUnits: ArmyListUnit[],
  armyListUnitOptions: ArmyListUnitOption[],
  armyListUnit: ArmyListUnit,
  modelRules: ModelRule[],
  magicItems: MagicItem[],
  magicStandards: MagicStandard[],
  unitOptionGroup: UnitOptionGroup,
): Array<OptionView> => {
  if (!armyList || !armyListUnit) return [];

  let options = unitOptionGroup.options
    .filter((option) => option.parent_id == null)
    .filter((item) =>
      optionIsActive(
        armyListUnit,
        armyList,
        armyListUnitOptions,
        item,
        unitOptionGroup.options,
      ),
    )
    .flatMap((option) =>
      optionPresenter(
        armyListUnit,
        armyList,
        armyListUnitOptions,
        0,
        unitOptionGroup,
        armyListUnit.mount,
        option,
        null,
        modelRules,
        magicItems,
        magicStandards,
      ),
    );

  const usedOptions = armyListUnits.map((unit) => {
    return {
      armyListUnitId: unit.id,
      options: unit.army_list_unit_unit_options.map((option) => ({
        id: option.unit_option_id,
        quantity: option.quantity,
      })),
    };
  });

  options = options.map((option) => {
    let min = usedOptions
      .filter((unit) => unit.armyListUnitId != armyListUnit.id)
      .flatMap((unit) => unit.options)
      .filter((item) => item.id === option.id)
      .map((item) => item.quantity)
      .reduce((a, b) => a + b, 0);
    let current = min;
    let quantity = option.isPerModel ? armyListUnit.size : 1;

    const optionChecked = armyListUnit.army_list_unit_unit_options.find(
      (item) => item.unit_option_id === option.id && !item.isDeleted,
    );
    if (optionChecked) {
      quantity = option.isPerModel ? armyListUnit.size : optionChecked.quantity;
      current += quantity;
      option.status.isChecked = true;
    }

    option.quantity = quantity != 0 ? quantity : null;
    option.points =
      option.points != null
        ? ({
            current: option.points.source * Math.max(quantity, 1),
            source: option.points.source,
            isLimit: option.points.isLimit,
          } satisfies OptionPointView as OptionPointView)
        : null;

    option.multiple!.min = min;
    option.multiple!.current = current;
    option.multiple!.delta = option.multiple!.max! - min;

    option.status.isVisible = true;

    return option;
  });

  for (let i = 0; i < options.length; i++) {
    if (!options[i].parentId) continue;

    const parent = options.find((item) => item.id === options[i].parentId);
    if (!parent) continue;

    options[i].status.isVisible =
      (parent.points == null && parent.status.isVisible) ||
      parent.status.isChecked ||
      parent.points?.isLimit === true;

    options[i].status.isHidden = !options[i].status.isVisible;
  }

  return options.filter(
    (item) =>
      item.status.isVisible || item.status.isChecked || item.status.isHidden,
  );
};
